import React from "react";

// `warning` is what sla_detector writes for a breach inside twice the SLA
// window; `critical` is what it writes beyond it. It was missing from this
// union, so every warning-level SLA breach fell to the `info` fallback and
// rendered in the neutral grey reserved for the lowest level — with the
// amber it should have had sitting unused two lines below, on `high`.
type SeverityLevel = 'critical' | 'high' | 'warning' | 'medium' | 'low' | 'info';

interface SeverityBadgeProps {
  // `string`, not SeverityLevel, because this is whatever the API returned and
  // the API's severity is a free-form string. Declaring the narrow type here
  // did not make the value narrow; it only made call sites reach for `as any`,
  // which is how the missing `warning` key went unnoticed. The runtime
  // fallback below is the real guard, and a backend drift test pins the
  // collector's vocabulary against the keys of `badgeClasses`.
  level: string;
}

/**
 * Severity maps onto the semantic status families rather than raw colours, so
 * each level keeps a readable contrast ratio in every theme. The families are
 * deliberately distinct from `accent`: brand orange means "interactive" here,
 * and reusing it for "high severity" would make buttons look like warnings.
 */
const badgeClasses: Record<SeverityLevel, string> = {
  critical: "bg-danger-subtle text-danger-fg border-danger-border",
  high:     "bg-warning-subtle text-warning-fg border-warning-border",
  warning:  "bg-warning-subtle text-warning-fg border-warning-border",
  medium:   "bg-caution-subtle text-caution-fg border-caution-border",
  low:      "bg-info-subtle text-info-fg border-info-border",
  info:     "bg-neutral-subtle text-neutral-fg border-neutral-border",
};

export default function SeverityBadge({ level }: SeverityBadgeProps) {
  // An unrecognised level from the API must not render an unstyled badge.
  const classes = badgeClasses[level as SeverityLevel] ?? badgeClasses.info;

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${classes}`}
    >
      {level}
    </span>
  );
}
